import { useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useLocation } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Sparkles, Clock, Award, CheckCircle, XCircle, Loader2, Wand2, Undo2, Lightbulb, ImageIcon, HelpCircle, AlertCircle, Info, CalendarClock } from 'lucide-react';
import toast from 'react-hot-toast';
import { certificationService, type ContentSelectionValidationResult } from '@/services/certification.service';
import { documentStoreService } from '@/services/document-store.service';
import usePermissionRouteAccess from '@/hooks/usePermissionRouteAccess';
import useHasPermission from '@/hooks/useHasPermission';
import { CERTIFICATION_CREATION_PERMISSION } from '@/constants/constant-permissions';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { Select } from '@/components/ui/Select';
import { Toggle } from '@/components/ui/Toggle';
import { CourseMultiSelect } from '@/components/certifications/CourseMultiSelect';
import { LearningPathMultiSelect } from '@/components/certifications/LearningPathMultiSelect';
import { ExamConfigSection } from '@/components/certifications/ExamConfigSection';
import Pagination from '@/components/ui/Pagination';
import { cleanText } from '@/utils/sanitize';

interface CertificationFormData {
  user_input: string; // Changed from certification_name - AI generates name from this
  targeted_audience: string;
  required_course_ids: string[];
  learning_path_ids: string[];
  // Knowledge store grounding — optional third content source. Any combination of
  // courses / learning paths / knowledge store is valid; at least one is required.
  knowledge_store_id: string;
  knowledge_store_only_mode: boolean;
  content_language: string;
  language_translation_mode: string;
  exam_config: {
    total_questions: number;
    question_types: string[];
    time_limit_minutes: number;
    passing_score: number;
    difficulty_distribution: {
      easy_percentage: number;
      medium_percentage: number;
      hard_percentage: number;
    };
    shuffle_questions: boolean;
    shuffle_options: boolean;
    show_feedback: boolean;
    show_correct_answers: boolean;
    allow_retry: boolean;
    max_attempts: number;
  };
  validity_type?: 'LIFETIME' | 'FIXED_DURATION';
  validity_months?: number;
}

interface GenerationStepStatus {
  status: 'pending' | 'in_progress' | 'processing' | 'completed' | 'failed';
  started_at?: Date;
  completed_at?: Date;
  duration_ms?: number;
  error?: string;
}

interface CertificationGenerationStatus {
  overall_status: 'pending' | 'processing' | 'in_progress' | 'completed' | 'failed';
  metadata_generation?: GenerationStepStatus;
  image_generation?: GenerationStepStatus;
  question_generation?: GenerationStepStatus;
  question_progress?: {
    total_questions: number;
    generated: number;
  };
  started_at?: Date;
  completed_at?: Date;
  token_usage?: number;
  error_message?: string;
}

interface RecentCertification {
  _id: string;
  certification_name: string;
  description?: string;
  targeted_audience?: string;
  status: 'DRAFT' | 'ACTIVE' | 'INACTIVE' | 'ARCHIVED';
  version: number;
  exam_config: {
    total_questions: number;
    time_limit_minutes?: number;
    passing_score: number;
  };
  certification_exam_id?: string;
  total_enrolled: number;
  total_certified: number;
  createdAt: Date;
  updatedAt: Date;
  generation_status?: CertificationGenerationStatus;
}

export default function CreateCertification() {
  const navigate = useNavigate();
  const location = useLocation();

  // Permission guards
  usePermissionRouteAccess([
    CERTIFICATION_CREATION_PERMISSION.CERTIFICATION_CREATION_READ,
    CERTIFICATION_CREATION_PERMISSION.CERTIFICATION_CREATION_WRITE,
  ]);
  const canWrite = useHasPermission([CERTIFICATION_CREATION_PERMISSION.CERTIFICATION_CREATION_WRITE]);
  const ticketDescription = location.state?.ticketDescription;
  const ticketLanguageCode = location.state?.languageCode;
  const certificationCourses = location.state?.certificationCourses;
  const certificationLearningPaths = location.state?.certificationLearningPaths;
  const [recentPage, setRecentPage] = useState(1);
  const [recentItemsPerPage, setRecentItemsPerPage] = useState(6);
  const [isEnhancing, setIsEnhancing] = useState(false);

  // Pre-flight relevance check. Runs on submit rather than on every keystroke: it costs an LLM
  // call, and the description is only complete at submit time. `overrideSelectionCheck` lets the
  // admin proceed after seeing the result — their selection is a deliberate choice, and this
  // check is an aid, not an authority.
  const [selectionCheck, setSelectionCheck] = useState<ContentSelectionValidationResult | null>(null);
  const [isCheckingSelection, setIsCheckingSelection] = useState(false);
  const overrideSelectionCheck = useRef(false);
  const [originalTextBeforeEnhancement, setOriginalTextBeforeEnhancement] = useState<string>('');

  const {
    control,
    handleSubmit,
    setValue,
    register,
    watch,
    getValues,
    trigger,
    formState: { errors, isSubmitting },
  } = useForm<CertificationFormData>({
    mode: 'all', // Validate on all events (change, blur, submit)
    defaultValues: {
      user_input: '', // AI will generate name, description, outcomes, tags from this
      targeted_audience: '',
      required_course_ids: [],
      learning_path_ids: [],
      knowledge_store_id: '',
      knowledge_store_only_mode: false,
      content_language: 'en-US',
      language_translation_mode: 'HYBRID',
      validity_type: 'LIFETIME',
      validity_months: 12,
      exam_config: {
        total_questions: 30,
        question_types: [],
        time_limit_minutes: 60,
        passing_score: 70,
        difficulty_distribution: {
          easy_percentage: 40,
          medium_percentage: 40,
          hard_percentage: 20,
        },
        shuffle_questions: true,
        shuffle_options: true,
        show_feedback: true,
        show_correct_answers: true,
        allow_retry: true,
        max_attempts: 3,
      },
    },
  });

  // Watch difficulty percentages to validate total must equal 100%
  const easyPercentage = watch('exam_config.difficulty_distribution.easy_percentage') || 0;
  const mediumPercentage = watch('exam_config.difficulty_distribution.medium_percentage') || 0;
  const hardPercentage = watch('exam_config.difficulty_distribution.hard_percentage') || 0;
  const totalDifficultyPercentage = Number(easyPercentage) + Number(mediumPercentage) + Number(hardPercentage);
  const isDifficultyInvalid = totalDifficultyPercentage !== 100;

  // True when at least one course or learning path is selected. When false and a knowledge store
  // IS selected, the exam is generated purely from documents, so Only Mode is the sensible
  // default — surfaced to the admin rather than applied invisibly.
  const selectedCourseIds = watch('required_course_ids') || [];
  const selectedLearningPathIds = watch('learning_path_ids') || [];
  const hasContentSource = selectedCourseIds.length > 0 || selectedLearningPathIds.length > 0;
  const selectedKnowledgeStoreId = watch('knowledge_store_id');
  const validityType = watch('validity_type') || 'LIFETIME';
  const onlyModeTouchedRef = useRef(false);

  // Retire the previous check whenever what it judged changes.
  //
  // Both pieces of state describe ONE specific selection. Without this, "Generate anyway" followed by
  // a failed create (validation, network) left the override latched for the life of the form: the
  // admin could replace every course and document and the check would never run again, while the
  // panel still listed the old flagged items with the old reasons.
  useEffect(() => {
    overrideSelectionCheck.current = false;
    setSelectionCheck(null);
  }, [
    selectedCourseIds.join(','),
    selectedLearningPathIds.join(','),
    selectedKnowledgeStoreId,

    watch('user_input'),
  ]);

  // Whether EVERY document was judged unrelated, which changes what continuing produces.
  //
  // Some documents matching and no document matching lead to opposite outcomes, and the panel used to
  // promise the first one in both cases. With at least one match, only-mode limits the exam to those
  // documents and it gets shorter. With NO match, the backend skips document retrieval altogether and
  // the selected course or learning path carries the exam at the FULL requested count — so telling the
  // admin to expect fewer questions was simply untrue.
  //
  // A learning path is flattened into its courses server-side, so it satisfies the same condition.
  const onlyModeOn = !!selectedKnowledgeStoreId && !!watch('knowledge_store_only_mode');
  // `items?.` as well as `selectionCheck?.` — the second guard is the one that matters. Optional
  // chaining already short-circuits the whole expression when selectionCheck is null, so that case
  // was never at risk; a malformed 200 carrying no items array is, since `?.` on the object does not
  // protect a property access further down the chain.
  const documentVerdicts = selectionCheck?.items?.filter(item => item.type === 'document') ?? [];
  const noDocumentMatches =
    documentVerdicts.length > 0 && documentVerdicts.every(item => !item.relevant);

  // What is inside each selected learning path — information, never a verdict.
  //
  // Filtered on `note`, deliberately NOT on `relevant`. A path is normally judged relevant, so the
  // flagged-items list below drops it — yet its contents are precisely what the admin has not seen.
  // A path is a curated collection and generation flattens it into every course it holds, so an
  // exam built from "Frontend Development" asks about React whether or not the description
  // mentioned React. The courses inside are not judged and are never flagged, because a spread of
  // subjects within one path is normal and correct; naming them up front turns a later "why did
  // this question come?" into something the admin agreed to in advance.
  const learningPathNotes =
    selectionCheck?.items?.filter(item => item.type === 'learning_path' && !!item.note) ?? [];

  // Only Mode follows the content sources until the admin touches the toggle, then stops — the
  // standard "smart default that yields to an explicit choice" pattern.
  //
  // Documents alone => on, because an admin who selects only documents means "examine my
  // documents"; a shorter exam whose every answer is verifiable beats a longer one padded with
  // general knowledge. Courses present => off, matching course generation's hybrid default.
  //
  // The toggle is never disabled. Locking it was wrong: "use my policy for our rules, general
  // knowledge for the rest" is a legitimate request, and a control that cannot be changed should
  // not be rendered as a control.
  useEffect(() => {
    if (onlyModeTouchedRef.current) return;
    setValue('knowledge_store_only_mode', !!selectedKnowledgeStoreId && !hasContentSource);
  }, [selectedKnowledgeStoreId, hasContentSource, setValue]);

  // Pre-fill from ticket data (from self-ticket request)
  useEffect(() => {
    if (ticketDescription) {
      setValue('user_input', ticketDescription, { shouldValidate: true });
    }
    if (ticketLanguageCode) {
      setValue('content_language', ticketLanguageCode);
    }
    if (certificationCourses && certificationCourses.length > 0) {
      const courseIds = certificationCourses.map((c: { item_id: string }) => c.item_id);
      setValue('required_course_ids', courseIds, { shouldValidate: true });
    }
    if (certificationLearningPaths && certificationLearningPaths.length > 0) {
      const lpIds = certificationLearningPaths.map((lp: { item_id: string }) => lp.item_id);
      setValue('learning_path_ids', lpIds, { shouldValidate: true });
    }
  }, [ticketDescription, ticketLanguageCode, certificationCourses, certificationLearningPaths, setValue]);

  const createMutation = useMutation({
    mutationFn: (data: any) => certificationService.createCertification(data),
    onSuccess: (response) => {
      if (response.success && response.data?._id) {
        toast.success('Certification generation started!');
        // Navigate immediately to generation status page
        navigate(`/admin/certifications/generate/${response.data._id}`);
      } else if (response.success) {
        toast.success('Certification created successfully!');
        navigate('/admin/certifications');
      }
    },
    onError: (error: any) => {
      toast.error(
        error?.response?.data?.error?.error_description ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to create certification'
      );
    },
  });

  // Fetch recent certifications
  const { data: recentData, isLoading: isLoadingRecent } = useQuery({
    queryKey: ['recent-certifications-create', recentPage, recentItemsPerPage],
    queryFn: () => certificationService.getRecentCertifications({
      limit: recentItemsPerPage,
      skip: (recentPage - 1) * recentItemsPerPage,
    }),
  });

  // Knowledge stores available for grounding. Only fully-processed stores are returned by
  // the dropdown endpoint, so an unindexed store can never be selected.
  const { data: documentStoresData } = useQuery({
    queryKey: ['document-stores-dropdown'],
    queryFn: () => documentStoreService.getDropdownList(),
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  // Helper functions
  const formatDate = (dateString: string | Date) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();

    // Ensure time differences are never negative - handle timing issues gracefully
    const diffMins = Math.max(0, Math.floor(diffMs / 60000));
    const diffHours = Math.max(0, Math.floor(diffMs / 3600000));
    const diffDays = Math.max(0, Math.floor(diffMs / 86400000));

    if (diffMins < 60) return `${diffMins} min${diffMins !== 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getStatusColor = (cert: RecentCertification) => {
    const status = cert.generation_status?.overall_status;
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-700 border-green-300';
      case 'processing':
      case 'in_progress':
        return 'bg-blue-100 text-blue-700 border-blue-300';
      case 'failed':
        return 'bg-red-100 text-red-700 border-red-300';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-300';
    }
  };

  const getStatusIcon = (cert: RecentCertification) => {
    const status = cert.generation_status?.overall_status;
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-3.5 h-3.5" />;
      case 'processing':
      case 'in_progress':
        return <Loader2 className="w-3.5 h-3.5 animate-spin" />;
      case 'failed':
        return <XCircle className="w-3.5 h-3.5" />;
      default:
        return <Clock className="w-3.5 h-3.5" />;
    }
  };

  const getStatusText = (cert: RecentCertification) => {
    const status = cert.generation_status?.overall_status;
    if (status === 'processing' || status === 'in_progress') return 'Generating';
    if (status === 'completed') return 'Completed';
    if (status === 'failed') return 'Failed';
    return 'Pending';
  };

  const handleRecentItemsPerPageChange = (newItemsPerPage: number) => {
    setRecentItemsPerPage(newItemsPerPage);
    setRecentPage(1); // Reset to first page when changing items per page
  };

  const onSubmit = async (data: CertificationFormData) => {
    const has_courses = data.required_course_ids && data.required_course_ids.length > 0;
    const has_learning_paths = data.learning_path_ids && data.learning_path_ids.length > 0;
    const has_knowledge_store = !!data.knowledge_store_id;

    // Any combination is valid — courses, learning paths, a knowledge store, or all three.
    // At least one content source is required for the AI to generate questions from.
    if (!has_courses && !has_learning_paths && !has_knowledge_store) {
      toast.error('Please select at least one course, learning path, or knowledge store');
      return;
    }

    if (!data.user_input || data.user_input.trim().length < 100) {
      toast.error('Please provide a detailed description (at least 100 characters)');
      return;
    }

    if (!data.exam_config.question_types || data.exam_config.question_types.length === 0) {
      toast.error('Please select at least one question type');
      return;
    }

    // Check the selection matches the description, unless the admin has already seen the result
    // and chosen to continue. Never blocks on its own failure: the backend returns 'ok' when it
    // cannot run, and a thrown request is swallowed here so creation is never lost to this check.
    //
    // `selectionCheck` counts as having seen it, not just the explicit "Generate anyway" button.
    // Without that, an admin who read the panel and then pressed the main Generate button below
    // re-entered here with the override still false, paid for another LLM call, got the same verdict,
    // and landed back on the same panel — able to loop indefinitely, one call per press, with nothing
    // indicating that the lower button would never work. The panel is cleared whenever the selection
    // changes, so this cannot skip a check the admin has not actually seen.
    if (!overrideSelectionCheck.current && !selectionCheck) {
      setIsCheckingSelection(true);
      try {
        const check = await certificationService.validateContentSelection({
          user_input: cleanText(data.user_input),
          required_course_ids: has_courses ? data.required_course_ids : undefined,
          learning_path_ids: has_learning_paths ? data.learning_path_ids : undefined,
          knowledge_store_id: has_knowledge_store ? data.knowledge_store_id : undefined,
        });

        if (check.outcome !== 'ok') {
          setSelectionCheck(check);
          setIsCheckingSelection(false);
          // Stop here so the admin reads the panel. The next submit carries the override.
          return;
        }
        setSelectionCheck(null);
      } catch {
        // Check unavailable — proceed. An aid must never cost the admin their work.
      }
      setIsCheckingSelection(false);
    }

    const payload = {
      user_input: cleanText(data.user_input),
      targeted_audience: cleanText(data.targeted_audience) || undefined,
      required_course_ids: data.required_course_ids && data.required_course_ids.length > 0 ? data.required_course_ids : undefined,
      learning_path_ids: data.learning_path_ids && data.learning_path_ids.length > 0 ? data.learning_path_ids : undefined,
      // Only send the knowledge store fields when a store is actually selected, so existing
      // course-only certifications produce an identical request body to before.
      knowledge_store_id: has_knowledge_store ? data.knowledge_store_id : undefined,
      knowledge_store_only_mode: has_knowledge_store ? data.knowledge_store_only_mode : undefined,
     
      // A clean selection sends undefined, leaving the request body identical to before.
      excluded_content_ids: selectionCheck?.outcome === 'warn'
        ? selectionCheck.items.filter(item => !item.relevant).map(item => item.id)
        : undefined,
      content_language: data.content_language,
      language_translation_mode: data.language_translation_mode,
      validity_type: data.validity_type,
      validity_months: data.validity_type === 'FIXED_DURATION' ? data.validity_months : undefined,
      exam_config: {
        total_questions: data.exam_config.total_questions,
        question_types: data.exam_config.question_types,
        difficulty_distribution: {
          easy_percentage: data.exam_config.difficulty_distribution.easy_percentage,
          medium_percentage: data.exam_config.difficulty_distribution.medium_percentage,
          hard_percentage: data.exam_config.difficulty_distribution.hard_percentage,
        },
        time_limit_minutes: data.exam_config.time_limit_minutes,
        passing_score: data.exam_config.passing_score,
        shuffle_questions: data.exam_config.shuffle_questions,
        shuffle_options: data.exam_config.shuffle_options,
        show_feedback: data.exam_config.show_feedback,
        show_correct_answers: data.exam_config.show_correct_answers,
        allow_retry: data.exam_config.allow_retry,
        max_attempts: data.exam_config.max_attempts,
      },
    };

    createMutation.mutate(payload);
  };

  return (
    <div className="space-y-6 max-w-5xl mx-auto">
      {/* Header */}
      <div>
        <div className="flex items-center gap-3">
          <div className="p-3 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl">
            <Sparkles className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Create AI-Powered Certification</h1>
            <p className="text-gray-600">Simply describe what you want - AI generates everything automatically</p>
          </div>
        </div>
      </div>

      {/* AI Info Alert */}
      <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-lg p-4">
        <div className="flex gap-3">
          <Sparkles className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="font-semibold text-purple-900 mb-1">No Certification Name Required!</h3>
            <p className="text-sm text-purple-800 mb-2">
              Just describe what the certification should cover, and AI will automatically generate:
            </p>
            <ul className="text-sm text-purple-800 space-y-1 ml-4">
              <li>• Professional certification name</li>
              <li>• Unique URL-friendly slug (always in English)</li>
              <li>• Comprehensive description and learning outcomes</li>
              <li>• Relevant categorization tags</li>
              <li>• Banner and thumbnail images</li>
              {/* Courses are no longer the only source. A knowledge store can ground the exam in the
                  organisation's own documents, and it can be selected on its own — so naming only
                  courses here understated the feature and read as a limitation. */}
              <li>• Exam questions from your selected courses, learning paths and documents</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* AI Generation Input */}
        <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles className="w-5 h-5 text-purple-600" />
            <h2 className="text-lg font-semibold text-gray-900">AI Generation Input</h2>
          </div>
          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-sm font-medium text-gray-700">
                  Describe Your Certification <span className="text-red-500">*</span>
                </label>
                <div className="flex items-center gap-2">
                  {/* Undo button - shows only if text was enhanced */}
                  {originalTextBeforeEnhancement && (
                    <button
                      type="button"
                      onClick={() => {
                        setValue('user_input', originalTextBeforeEnhancement, { shouldValidate: true });
                        setOriginalTextBeforeEnhancement('');
                        toast.success('Reverted to original text');
                      }}
                      className="p-2 rounded-lg transition-all bg-gray-100 hover:bg-gray-200 text-gray-600"
                      title="Undo enhancement"
                    >
                      <Undo2 className="w-4 h-4" />
                    </button>
                  )}

                  {/* Enhance button */}
                  <button
                    type="button"
                    onClick={async () => {
                      const currentText = watch('user_input');

                      if (!currentText || currentText.trim().length < 10) {
                        toast.error('Please enter at least 10 characters to enhance');
                        return;
                      }

                      setIsEnhancing(true);
                      const loadingToast = toast.loading('Enhancing your certification goal with AI...');

                      try {
                        const result = await certificationService.enhanceUserInput({
                          user_input: cleanText(currentText),
                          targeted_audience: cleanText(watch('targeted_audience')) || undefined,
                        });

                        setOriginalTextBeforeEnhancement(currentText);
                        setValue('user_input', result.enhanced_input, { shouldValidate: true });
                        toast.dismiss(loadingToast);
                        toast.success('Certification goal enhanced successfully!');
                      } catch (error: any) {
                        toast.dismiss(loadingToast);
                        toast.error(
                          error?.response?.data?.error?.error_description ||
                          error?.response?.data?.message ||
                          error?.message ||
                          'Failed to enhance input'
                        );
                        console.error(error);
                      } finally {
                        setIsEnhancing(false);
                      }
                    }}
                    disabled={!canWrite || isEnhancing || !watch('user_input') || watch('user_input')?.trim().length < 10}
                    title={!canWrite ? 'You do not have permission to perform this action' : undefined}
                    className="flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all text-sm font-medium"
                  >
                    {isEnhancing ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Enhancing...
                      </>
                    ) : (
                      <>
                        <Wand2 className="w-4 h-4" />
                        Enhance with AI
                      </>
                    )}
                  </button>
                </div>
              </div>
              <Textarea<CertificationFormData>
                name="user_input"
                control={control}
                rules={{
                  required: 'Please describe what the certification should cover',
                  minLength: { value: 100, message: 'Description must be at least 100 characters' },
                  maxLength: { value: 1000, message: 'Description cannot exceed 1000 characters' },
                }}
                rows={6}
                maxLength={1000}
                placeholder="Example: Create a certification for cloud engineers focusing on AWS services, Kubernetes container orchestration, Infrastructure as Code with Terraform, and cloud security best practices. The certification should validate expertise in designing scalable cloud architectures and implementing DevOps workflows."
              />
              <p className="text-xs text-gray-500 mt-1">
                {watch('user_input')?.length || 0}/1000 characters. Be specific about technologies, skills, and target audience.
              </p>
            </div>

            <Input
              name="targeted_audience"
              control={control}
              label="Target Audience (Optional)"
              placeholder="e.g., Senior Cloud Engineers, DevOps Professionals"
              helperText="Leave empty to let AI determine from course content"
              error={errors.targeted_audience?.message}
            />
          </div>
        </div>

        {/* Content Sources: Courses / Learning Paths / Knowledge Store */}
        <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
          <div className="mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Content Sources</h2>
            <p className="text-sm text-gray-600 mt-1">
              Choose where the exam questions should come from. You can select courses, learning paths, a knowledge store, or any combination — at least one is required (courses are automatically extracted from learning paths).
            </p>

            {/* States up front that courses beat a contradicting description. A per-case detector
                would also fire on legitimate multi-subject certifications. */}
            <div className="mt-3 flex items-start gap-2 rounded-md border border-gray-200 bg-gray-50 p-3">
              <HelpCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-gray-400" />
              <div className="text-xs leading-relaxed text-gray-600">
                <span className="font-medium text-gray-700">How your selection is used.</span>{' '}
                Questions are generated from the sources you select here. Your description sets the
                certification title and, when a knowledge store is selected, determines which of its
                documents are used. If a course is attached, the exam follows that course even where
                the description suggests another subject.
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <CourseMultiSelect
              name="required_course_ids"
              control={control}
              label="Courses (Optional)"
              required={false}
              helperText="Select individual courses for this certification"
              error={errors.required_course_ids?.message}
            />

            <LearningPathMultiSelect
              name="learning_path_ids"
              control={control}
              label="Learning Paths (Optional)"
              required={false}
              helperText="Select learning paths - all courses from selected paths will be included automatically"
              error={errors.learning_path_ids?.message}
            />

            {/* Knowledge Store Grounding */}
            <div className="pt-4 border-t border-gray-100 space-y-3">
              <div>
                <label className="block text-sm font-semibold text-gray-900">
                  Knowledge Store (Optional)
                </label>
                <p className="text-xs text-gray-600 mt-1">
                  The AI will take the facts for each question from your own uploaded documents (policies, manuals, handbooks) instead of from its own general knowledge — so the correct answers match your organisation's actual rules.
                </p>
              </div>

              <Select
                name="knowledge_store_id"
                control={control}
                label="Select Knowledge Store"
                error={errors.knowledge_store_id?.message}
                options={[
                  { label: 'None - Use course content and general AI knowledge', value: '' },
                  ...(documentStoresData?.map((store: { _id: string; name: string }) => ({
                    label: store.name,
                    value: store._id,
                  })) || []),
                ]}
                placeholder="Choose a knowledge store..."
              />

              {/* Options only appear once a store is chosen */}
              {watch('knowledge_store_id') && (
                <div className="space-y-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  {/* Defaults ON for documents-only, never locked — documents plus general
                      knowledge is a valid choice. */}
                  <Toggle
                    name="knowledge_store_only_mode"
                    control={control}
                    label="Use only my documents"
                    description="Decides whether the AI may add facts that are not in your uploaded documents."
                    onChange={() => { onlyModeTouchedRef.current = true; }}
                  />

                  {/* Bullets rather than one long description — admins couldn't tell which way to
                      set it. Third bullet is the fewer-questions consequence. */}
                  <ul className="space-y-1.5 text-xs leading-relaxed text-gray-600">
                    <li className="flex items-start gap-2">
                      <span className="mt-[7px] h-1.5 w-1.5 flex-shrink-0 rounded-full bg-gray-400" />
                      <span>
                        <span className="font-semibold text-gray-800">On</span> — Questions are
                        created <span className="font-medium text-gray-700">only from your uploaded
                        documents</span>. Nothing is added from the AI&apos;s own knowledge. Best for
                        policy, compliance and internal process exams.
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="mt-[7px] h-1.5 w-1.5 flex-shrink-0 rounded-full bg-gray-400" />
                      <span>
                        <span className="font-semibold text-gray-800">Off</span> — Your documents are
                        used first, then the AI{' '}
                        <span className="font-medium text-gray-700">fills the gaps with its general
                        knowledge</span>. Best when you want wider coverage of the subject.
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="mt-[7px] h-1.5 w-1.5 flex-shrink-0 rounded-full bg-gray-400" />
                      <span>
                        If your documents cover only a few topics, you may get{' '}
                        <span className="font-medium text-gray-700">fewer questions than you asked
                        for</span> — the questions match what your documents actually contain. The
                        final number and the reason are shown once generation completes.
                      </span>
                    </li>
                  </ul>

                  {/* Shown whenever the setting is off. A course contributes titles only, not
                      lesson text, so attaching one does not make answers traceable. */}
                  {!watch('knowledge_store_only_mode') && (
                    <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2 flex items-start gap-1.5">
                      <HelpCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                      <span>
                        This is off, so some answers will come from the AI&apos;s general knowledge
                        and will not be traceable to an uploaded document. Turn it on if every
                        answer must be verifiable in your documents.
                      </span>
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Certificate Validity: same choice as Edit Settings, saved on the new certification */}
        <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
          <div className="flex items-center gap-3 pb-4 mb-4 border-b border-gray-200">
            <div className="p-2 bg-teal-100 rounded-lg">
              <CalendarClock className="w-5 h-5 text-teal-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Certificate Validity</h2>
              <p className="text-sm text-gray-500">How long certificates from this certification stay valid</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {([
              { value: 'LIFETIME', label: 'Lifetime' },
              { value: 'FIXED_DURATION', label: 'Fixed duration' },
            ] as const).map(option => (
              <button
                key={option.value}
                type="button"
                aria-pressed={validityType === option.value}
                onClick={() => {
                  setValue('validity_type', option.value);
                  // Re-check months so an old error clears when switching to Lifetime
                  trigger('validity_months');
                }}
                className={`px-4 py-2 rounded-xl text-sm font-medium border transition-colors ${validityType === option.value
                  ? 'bg-teal-50 border-teal-500 text-teal-700'
                  : 'bg-white border-gray-300 text-gray-600 hover:border-gray-400'}`}
              >
                {option.label}
              </button>
            ))}
          </div>
          {validityType === 'FIXED_DURATION' && (
            <div className="mt-3">
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min="1"
                  max="120"
                  step="1"
                  aria-label="Validity in months"
                  {...register('validity_months', {
                    valueAsNumber: true,
                    validate: (months) => getValues('validity_type') !== 'FIXED_DURATION'
                      || (Number.isInteger(months) && months! >= 1 && months! <= 120)
                      || 'Enter a whole number of months between 1 and 120'
                  })}
                  className={`w-28 border rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500 hover:border-gray-400 transition-shadow ${errors.validity_months ? 'border-red-500' : 'border-gray-300'}`}
                  placeholder="12"
                />
                <span className="text-sm text-gray-600">months</span>
              </div>
              {errors.validity_months && (
                <p className="text-xs text-red-500 mt-1">{errors.validity_months.message}</p>
              )}
            </div>
          )}
          <p className="text-xs text-gray-400 mt-2 flex items-center gap-1">
            <Info className="w-3 h-3" />
            {validityType === 'FIXED_DURATION'
              ? 'Certificates expire this many months after issue (12 months = 1 year). You can change this later in Edit Settings.'
              : 'Certificates never expire. You can change this later in Edit Settings.'}
          </p>
        </div>

        {/* Exam Configuration */}
        <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
          <ExamConfigSection
            control={control}
            setValue={setValue}
            errors={errors}
          />

          {/* Pre-flight result. Shown after a submit that found a mismatch, so the admin reads it
              before spending a generation. "Generate anyway" is always offered — a block is a
              strong recommendation, not an authority, and a false block must never trap them. */}
          {selectionCheck && (
            <div
              className={`mt-6 rounded-lg border p-4 ${
                selectionCheck.outcome === 'block'
                  ? 'border-red-300 bg-red-50'
                  : 'border-amber-300 bg-amber-50'
              }`}
            >
              <div className="flex items-start gap-2">
                <AlertCircle
                  className={`mt-0.5 h-5 w-5 flex-shrink-0 ${
                    selectionCheck.outcome === 'block' ? 'text-red-600' : 'text-amber-600'
                  }`}
                />
                <div className="flex-1">
                  <p
                    className={`text-sm font-semibold ${
                      selectionCheck.outcome === 'block' ? 'text-red-900' : 'text-amber-900'
                    }`}
                  >
                    {selectionCheck.outcome === 'block'
                      ? 'Nothing you selected matches your description'
                      : 'Some selected content may not match your description'}
                  </p>

                  <p
                    className={`mt-1 text-xs ${
                      selectionCheck.outcome === 'block' ? 'text-red-800' : 'text-amber-800'
                    }`}
                  >
                    {selectionCheck.summary}
                  </p>

                  <ul className="mt-3 space-y-1.5">
                    {selectionCheck.items
                      .filter((item) => !item.relevant)
                      .map((item) => (
                        <li key={`${item.type}-${item.id}`} className="flex items-start gap-2 text-xs">
                          <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-gray-500" />
                          <span className="text-gray-800">
                            <span className="font-medium">{item.name}</span>
                            <span className="text-gray-500"> ({item.type.replace('_', ' ')})</span>
                            {' — '}
                            {item.reason}
                          </span>
                        </li>
                      ))}
                  </ul>

                  {/* What each selected learning path contains. Sits between the flagged items and
                      the consequence because it is neither: nothing here is wrong, it is the
                      context that makes the sentence below accurate. */}
                  {learningPathNotes.length > 0 && (
                    <div className="mt-3 space-y-2 rounded-md border border-gray-200 bg-white/70 p-2.5">
                      {learningPathNotes.map((item) => (
                        <div key={`note-${item.id}`} className="flex items-start gap-2">
                          <Info className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-gray-500" />
                          <p className="text-xs leading-relaxed text-gray-700">
                            <span className="font-medium text-gray-800">{item.name}</span>
                            {' — '}
                            {item.note}
                          </p>
                        </div>
                      ))}

                      {onlyModeOn && (
                        <p className="border-t border-gray-200 pt-2 text-xs leading-relaxed text-gray-600">
                          But <span className="font-medium">"Use only my documents"</span> is on, so
                          questions will come from your{' '}
                          <span className="font-medium">documents only</span>. If a topic is not in your
                          documents, there will be no question about it.
                        </p>
                      )}
                    </div>
                  )}

                  {/* State the CONSEQUENCE, not just the mismatch. "Continue if this is intentional"
                      left admins guessing what continuing would produce, and they were surprised to
                      receive an exam on the courses' subject instead of the one they described. */}
                  <div className="mt-3 rounded-md border border-gray-200 bg-white/70 p-2.5">
                    <p className="text-xs font-medium text-gray-800">If you continue</p>
                    {selectionCheck.outcome === 'block' ? (
                      <p className="mt-1 text-xs leading-relaxed text-gray-700">
                        Questions will be generated from the <span className="font-medium">content you
                        selected above</span> — not from your description. The exam will be about
                        whatever those courses and documents cover, and the certification title may
                        not match what you typed.
                      </p>
                    ) : onlyModeOn && noDocumentMatches && hasContentSource ? (
                      /* Only Mode on, but NOT ONE document matches, and a course or learning path is
                         selected. The backend skips document retrieval entirely and the course or
                         path carries the exam instead, so the setting cannot be honoured.

                         Deliberately no promise about the final count: this panel's verdict comes from
                         the relevance check, while the exam's own document filter is a separate
                         mechanism on different inputs, and the two can disagree. Stating "you will
                         receive the full number" would guarantee an outcome decided elsewhere. */
                      <p className="mt-1 text-xs leading-relaxed text-gray-700">
                        None of your documents appear to match your description, so they are likely to{' '}
                        <span className="font-medium">contribute nothing</span>. If that happens, the
                        exam is built from your selected course or learning path instead and you will
                        still receive the{' '}
                        <span className="font-medium">full number of questions you requested</span> —
                        but the answers{' '}
                        <span className="font-medium">cannot be verified in your documents</span>,
                        which is what "Use only my documents" is meant to guarantee. The finished
                        certification will state exactly what was used.
                      </p>
                    ) : onlyModeOn ? (
                      /* Only Mode with at least one matching document: those documents are the
                         ceiling, so dropping the unrelated ones genuinely shortens the exam. */
                      <p className="mt-1 text-xs leading-relaxed text-gray-700">
                        The items listed above will <span className="font-medium">not contribute
                        questions</span>. Because <span className="font-medium">"Use only my
                        documents"</span> is on, the exam is limited to the matching documents — so you
                        will likely receive <span className="font-medium">fewer questions than you
                        requested</span>.
                      </p>
                    ) : (
                      /* Toggle off, or no knowledge store: the count is NOT reduced. The rest of the
                         selection still contributes and AI fills whatever is left, so the honest
                         warning is about where those answers come FROM — not about a shorter exam. */
                      <p className="mt-1 text-xs leading-relaxed text-gray-700">
                        The items listed above will <span className="font-medium">not contribute
                        questions</span>. You will still receive the{' '}
                        <span className="font-medium">full number you requested</span> — the exam will
                        be built from the rest of your selection, with{' '}
                        <span className="font-medium">AI general knowledge</span> filling any gap.
                        Those AI-written questions cannot be verified against your content.
                      </p>
                    )}
                    <p className="mt-1.5 text-xs text-gray-600">
                      To get an exam about what you described, change your selection — or change your
                      description to match what you selected.
                    </p>
                  </div>

                  <button
                    type="submit"
                    onClick={() => { overrideSelectionCheck.current = true; }}
                    disabled={!canWrite || isSubmitting || createMutation.isPending}
                    className={`mt-4 inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold text-white shadow-sm transition-all hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 disabled:shadow-none ${
                      selectionCheck.outcome === 'block'
                        ? 'bg-red-600 hover:bg-red-700 focus-visible:ring-red-500'
                        : 'bg-amber-600 hover:bg-amber-700 focus-visible:ring-amber-500'
                    }`}
                  >
                    {isSubmitting || createMutation.isPending ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span>Generating…</span>
                      </>
                    ) : (
                      <span>Generate anyway</span>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

<div className="flex justify-end items-center gap-3 mt-6">
    <button
      type="button"
      onClick={() => navigate('/admin/certifications')}
      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
    >
      Cancel
    </button>

    <button
      type="submit"
      disabled={!canWrite || isSubmitting || createMutation.isPending || isDifficultyInvalid || isCheckingSelection}
      className="flex items-center gap-2 px-6 py-2 text-sm font-semibold text-white bg-gradient-to-r from-purple-600 to-indigo-600 rounded-lg hover:from-purple-700 hover:to-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md hover:shadow-lg"
      title={!canWrite ? 'You do not have permission to perform this action' : isDifficultyInvalid ? 'Difficulty distribution total must equal exactly 100%' : ''}
    >
      {isCheckingSelection ? (
        <>
          <Loader2 className="w-4 h-4 animate-spin" />
          <span>Checking your selection...</span>
        </>
      ) : isSubmitting || createMutation.isPending ? (
        <>
          <Loader2 className="w-4 h-4 animate-spin" />
          <span>Generating Certification...</span>
        </>
      ) : (
        <>
          <Sparkles className="w-4 h-4" />
          <span>Generate Certification with AI</span>
        </>
      )}
    </button>
  </div>
        </div> 
      </form>

      {/* Recent Certifications Section */}
      <div className="mt-8 sm:mt-12 lg:mt-16">
          <div className="text-center mb-4 sm:mb-6 lg:mb-8">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-1 sm:mb-2">Recent Certification Creation</h2>
            <p className="text-sm sm:text-base text-gray-600">
              {isLoadingRecent ? 'Loading your recent certification generations...' : 'Continue working on your recently created certifications'}
            </p>
          </div>

          {isLoadingRecent ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6">
              {[1, 2, 3].map((index) => (
                <div key={index} className="bg-white rounded-2xl border border-gray-100 p-6 animate-pulse">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1 pr-4">
                      <div className="h-4 bg-gray-200 rounded w-3/4 mb-2" />
                      <div className="h-3 bg-gray-200 rounded w-1/2" />
                    </div>
                    <div className="h-6 w-16 bg-gray-200 rounded-full" />
                  </div>
                  <div className="h-3 bg-gray-200 rounded w-full mb-2" />
                  <div className="h-3 bg-gray-200 rounded w-2/3 mb-4" />
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex gap-4">
                      <div className="h-3 w-16 bg-gray-200 rounded" />
                      <div className="h-3 w-14 bg-gray-200 rounded" />
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <div className="h-3 w-24 bg-gray-200 rounded" />
                      <div className="h-3 w-8 bg-gray-200 rounded" />
                    </div>
                    <div className="grid grid-cols-4 gap-2">
                      {[1, 2, 3, 4].map((i) => (
                        <div key={i} className="text-center">
                          <div className="w-full h-3 bg-gray-200 rounded-full" />
                          <div className="mt-1 h-3 w-4 bg-gray-200 rounded mx-auto" />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : recentData?.certifications && recentData.certifications.length > 0 ? (
            <div>
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6">
                {recentData.certifications.map((cert) => (
                  <div
                    key={cert._id}
                    className="group relative bg-white rounded-2xl border border-gray-100 transition-all duration-300 flex flex-col h-[320px] hover:shadow-xl hover:border-yellow-200 cursor-pointer transform hover:-translate-y-1"
                    onClick={() => navigate(`/admin/certifications/generate/${cert._id}`)}
                  >
                    {/* Card Content */}
                    <div className="p-6 pb-0 flex-1 flex flex-col">
                      {/* Title */}
                      <div className="mb-3">
                        <h3 className="font-semibold text-gray-900 line-clamp-2 text-base leading-tight">
                          {cert.certification_name || 'Untitled Certification'}
                        </h3>
                      </div>

                      {/* Status and Time Row */}
                      <div className="flex items-center justify-between mb-3">
                        <p className="text-sm text-gray-500 flex items-center gap-1">
                          <Clock className="w-3.5 h-3.5" />
                          {formatDate(cert.createdAt)}
                        </p>
                        <div className={`flex items-center gap-2 px-3 py-1.5 text-xs font-medium rounded-full border ${getStatusColor(cert)}`}>
                          {getStatusIcon(cert)}
                          <span className="capitalize">{getStatusText(cert)}</span>
                        </div>
                      </div>

                      {/* Description - 2 lines only */}
                      <div className="flex-1">
                        {cert.description && (
                          <p className="text-sm text-gray-600 mb-4 line-clamp-2 leading-relaxed">
                            {cert.description}
                          </p>
                        )}
                      </div>

                      {/* Certification Stats */}
                      <div className="flex items-center justify-between mb-4 text-sm text-gray-500">
                        <div className="flex items-center gap-4">
                          {cert.exam_config.total_questions > 0 && (
                            <span className="flex items-center gap-1.5">
                              <Award className="w-4 h-4 text-gray-400" />
                              <span className="font-medium">{cert.exam_config.total_questions}</span>
                              <span>questions</span>
                            </span>
                          )}
                          {cert.exam_config.time_limit_minutes && (
                            <span className="flex items-center gap-1.5">
                              <Clock className="w-4 h-4 text-gray-400" />
                              <span className="font-medium">{cert.exam_config.time_limit_minutes}</span>
                              <span>mins</span>
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Fixed Bottom Progress Bar Section */}
                    <div className="px-6 pb-6">
                      <div className="border-t border-gray-100 pt-4 space-y-3">
                        <div className="flex items-center justify-between text-xs font-medium text-gray-600">
                          <span>Generation Progress</span>
                          <span>
                            {[
                              cert.generation_status?.metadata_generation?.status,
                              cert.generation_status?.image_generation?.status,
                              cert.generation_status?.question_generation?.status
                            ].filter(status => status?.toLowerCase() === 'completed').length}/3
                          </span>
                        </div>

                        <div className="grid grid-cols-3 gap-2">
                          {[
                            {
                              status: cert.generation_status?.metadata_generation?.status,
                              label: 'Metadata',
                              icon: Lightbulb
                            },
                            {
                              status: cert.generation_status?.image_generation?.status,
                              label: 'Images',
                              icon: ImageIcon
                            },
                            {
                              status: cert.generation_status?.question_generation?.status,
                              label: 'Questions',
                              icon: HelpCircle,
                              progress: cert.generation_status?.question_progress
                            }
                          ].map((item, index) => {
                            const statusLower = item.status?.toLowerCase();
                            const bgColor =
                              statusLower === 'completed' ? 'bg-green-500' :
                              statusLower === 'processing' || statusLower === 'in_progress' ? 'bg-blue-500 animate-pulse' :
                              statusLower === 'failed' ? 'bg-red-500' :
                              'bg-gray-200';

                            const iconColor =
                              statusLower === 'completed' ? 'text-green-600' :
                              statusLower === 'processing' || statusLower === 'in_progress' ? 'text-blue-600' :
                              statusLower === 'failed' ? 'text-red-600' :
                              'text-gray-400';

                            const IconComponent = item.icon;

                            return (
                              <div key={index} className="text-center">
                                <div className="flex flex-col items-center gap-1.5">
                                  <IconComponent className={`w-5 h-5 ${iconColor} transition-colors duration-300`} />
                                  <div
                                    className={`w-full h-2 rounded-full transition-all duration-300 ${bgColor}`}
                                  />
                                  <span className="text-[10px] text-gray-500">
                                    {item.label}
                                    {item.progress && (statusLower === 'processing' || statusLower === 'in_progress') && (
                                      <span className="ml-1 font-medium text-blue-600">
                                        ({item.progress.generated}/{item.progress.total_questions})
                                      </span>
                                    )}
                                  </span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Recent Certifications Pagination */}
              {recentData && recentData.total > 0 && (
                <div className="mt-8 pt-6 border-t border-gray-200">
                  <Pagination
                    currentPage={recentPage}
                    totalPages={recentData.pagination.totalPages}
                    totalItems={recentData.total}
                    itemsPerPage={recentItemsPerPage}
                    onPageChange={setRecentPage}
                    onItemsPerPageChange={handleRecentItemsPerPageChange}
                    showItemsPerPage={true}
                    itemsPerPageOptions={[6, 12, 18, 24]}
                  />
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
              <Award className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No recent certifications</h3>
              <p className="text-gray-600">Your recently created certifications will appear here.</p>
            </div>
          )}
        </div>
    </div>
  );
}
